/**
 * Display formatting. Money in this system is *always* integer micros, never floats
 * (the contract stores `*_micros` as ints) — so the only place a currency string is
 * produced is here, and it is produced from micros.
 */
import type { Platform } from '@/api/types'

const currencyFormatters = new Map<string, Intl.NumberFormat>()

export function formatMoneyMicros(micros: number | null | undefined, currency = 'GBP'): string {
  if (micros === null || micros === undefined) return '—'
  const amount = micros / 1_000_000
  let formatter = currencyFormatters.get(currency)
  if (!formatter) {
    try {
      formatter = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency,
        maximumFractionDigits: amount < 100 ? 2 : 0,
      })
    } catch {
      formatter = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 })
    }
    currencyFormatters.set(currency, formatter)
  }
  return formatter.format(amount)
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return new Intl.NumberFormat('en-GB').format(value)
}

export function formatCompactNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  if (Math.abs(value) < 1_000) return formatNumber(value)
  return new Intl.NumberFormat('en-GB', { notation: 'compact', maximumFractionDigits: 1 }).format(
    value,
  )
}

export function formatPercent(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined) return '—'
  return `${(value * 100).toFixed(digits)}%`
}

export function formatRatio(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined) return '—'
  return value.toFixed(digits)
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'UTC',
  }).format(date)
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeZone: 'UTC' }).format(date)
}

export function formatRelative(value: string | null | undefined, now = Date.now()): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const deltaSeconds = Math.round((date.getTime() - now) / 1000)
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 7],
    ['week', 4.35],
    ['month', 12],
    ['year', Number.POSITIVE_INFINITY],
  ]
  let amount = deltaSeconds
  for (const [unit, size] of units) {
    if (Math.abs(amount) < size || unit === 'year') {
      return new Intl.RelativeTimeFormat('en-GB', { numeric: 'auto' }).format(
        Math.round(amount),
        unit,
      )
    }
    amount /= size
  }
  return value
}

export function platformLabel(platform: Platform | string): string {
  if (platform === 'google') return 'Google Ads'
  if (platform === 'microsoft') return 'Microsoft Ads'
  return String(platform)
}

/** 'campaign_budget' -> 'Campaign budget'; 'responsive_search_ad' -> 'Responsive search ad'. */
export function entityLabel(key: string): string {
  const spaced = key.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

export function truncate(text: string, max = 80): string {
  if (text.length <= max) return text
  return `${text.slice(0, max - 1)}…`
}

/** Value rendering for the diff tree: scalars plain, structures summarised. */
export function formatDiffValue(value: unknown, max = 140): { text: string; title: string } {
  if (value === null) return { text: 'null', title: 'null' }
  if (value === undefined) return { text: '—', title: 'absent' }
  if (typeof value === 'string') {
    const title = value
    return { text: value.length > max ? truncate(value, max) : value, title }
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return { text: String(value), title: String(value) }
  }
  if (Array.isArray(value)) {
    const title = JSON.stringify(value)
    return { text: `${value.length} item${value.length === 1 ? '' : 's'}`, title: truncate(title, 400) }
  }
  const title = JSON.stringify(value)
  return { text: truncate(title ?? 'object', max), title: truncate(title ?? 'object', 400) }
}

export function pluralise(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`
}
